import { Injectable } from '@angular/core';
import { IPaymentGatewayProvider } from "./payment-gateway-provider";
import { Observable, BehaviorSubject } from "rxjs";
import { PaymentModel, PaymentVoucher } from '../../models/payment-models/payment.model';
import { PaymentStatusModel } from '../../models/payment-models/payment-status.model';
import { ExternalPaymentModel, PaymentsModel } from '../../models/payment-models/external-payment.model';
import { NavigationService } from 'src/app/shared/utils/navigation';
import { environment } from 'src/environments/environment'
import { GATEWAY_PAYMENT_STATUS } from './payment-gateway-constant';
import { PaymentService } from '../payment-services';
import { PaymentConstant } from 'src/app/modules/order/shared';
import { PaysafeCheckOutRequestModel } from '../../models/payment-models/paysafe-checkout-request.model';
declare var paysafe: any;

@Injectable({
    providedIn: 'root'
})
export class PaySafeGatewayService implements IPaymentGatewayProvider {

    private readonly STATUS: string = "status";
    public readonly PAYMENT_CONSTANT = PaymentConstant;
    public readonly PAYMENT_STATUS_STORAGE_NAME = 'paymentStatus';
    private _responseStatus: string;
    private _responseParam: URLSearchParams;
    public observable$ = new BehaviorSubject<PaymentStatusModel>(null);

    constructor(private paymentService: PaymentService,
        private navigationService: NavigationService,) {
    }

    public getResponseStatus(): string {
        return this._responseStatus;
    }

    public resetResponseStatus(): void {
        this._responseStatus = null;
    }

    public getResponseParam(key: string): string {
        if (!this._responseParam) {
            return null;
        }
        return this._responseParam.get(key);
    }

    public processPayment(payment: ExternalPaymentModel): Observable<PaymentStatusModel> {
        const initialPayment = this.createInitialPayment(payment.recordLocator,
            payment.orderId,
            payment.amount.currencyCode,
            payment.amount.value,
            payment.organisationCode,
            payment.payments,
            payment.paymentVoucher);

        let paymentStatus = this.handlePayment(payment,
            initialPayment);
        return this.observable$;
    }

    private async handlePayment(payment: ExternalPaymentModel,
        initialPayment: PaymentModel): Promise<PaymentStatusModel> {
        let checkoutPaymentStatus: PaymentStatusModel = null;
        if (payment.payments.formOfPaymentTypeCode == this.PAYMENT_CONSTANT.cc) {
            let responses = await this.paymentService.getPaysafeCheckOutRequest(payment.orderId).toPromise();
            let environment = this.getEnvironment();
            var billingAddress = this.createBillinigAddress(responses);
            this.checkout(responses, initialPayment, this.paymentService, environment, this.PAYMENT_STATUS_STORAGE_NAME, billingAddress);
            let loop = setInterval(() => {
                if (this.paymentFinished()) {
                    if (!checkoutPaymentStatus) {
                        checkoutPaymentStatus = {} as PaymentStatusModel;
                        checkoutPaymentStatus.status = this.getPaymentStatus();
                    }
                    this.observable$.next(checkoutPaymentStatus);
                    clearInterval(loop);
                    sessionStorage.removeItem(this.PAYMENT_STATUS_STORAGE_NAME);
                    return checkoutPaymentStatus;
                }
            }, 500);
        }
        else {
            let responses = await this.paymentService.processPayment(initialPayment).toPromise();
            this.observable$.next(responses);
            return responses;
        }
    }

    private createInitialPayment(recordLocator: string,
        orderId: string,
        paymentCurrency: string,
        paymentAmount: number,
        organisationCode: string,
        payments: PaymentsModel,
        paymentVoucher: PaymentVoucher): PaymentModel {

        return {
            context: {
                language: "en",
                currencyCode: "EUR",
                organisationCode: organisationCode,
                organisationId: null
            },
            orderNumber: recordLocator,
            orderId: orderId,
            formOfPayments: null,
            total: {
                sales: {
                    currencyCode: paymentCurrency,
                    amount: paymentAmount
                }
            },
            payments: [payments],
            paymentVoucher: paymentVoucher,
            redirect: null
        };
    }
    public handlePaymentPageResponse(): void {
        return;
    }

    private checkout(responses: PaysafeCheckOutRequestModel,
        initialPayment: PaymentModel,
        service: PaymentService,
        environment: string,
        paymentStatusStoreRageName: string,
        billingAddress
    ) {
        let amount = Number((initialPayment.total.sales.amount * 100).toFixed(0));
        paysafe.checkout.setup(responses.apiKeyMerChant, {
            currency: initialPayment.total.sales.currencyCode,
            amount: amount,
            environment: environment,
            locale: "en_US",
            simulator: "EXTERNAL",
            customer: {
                firstName: responses.firstName,
                lastName: responses.lastName,
                email: responses.mailAddress
            },
            billingAddress: billingAddress,        
            merchantRefNum: responses.merchantRefNum,
            canEditAmount: false,
            displayPaymentMethods: ["card"],
            paymentMethodDetails: {
                card: {}
            }
        }, function (instance, error, result) {
            if (result) {
                initialPayment.payments[0].paymentToken = result.paymentHandleToken;
                initialPayment.payments[0].transactionReference = responses.merchantRefNum;
                let paymentStatus: PaymentStatusModel;
                service.processPayment(initialPayment)
                    .subscribe((response: PaymentStatusModel) => {
                        paymentStatus = response;
                        sessionStorage.setItem(paymentStatusStoreRageName, "success");
                        instance.showSuccessScreen();
                    }, () => {
                        if (!paymentStatus) {
                            instance.showFailureScreen();
                        }
                    }
                );
            } else {
                console.error(error);
                instance?.showFailureScreen();
            }
        }, function (stage, expired) {
            switch (stage) {
                case "PAYMENT_HANDLE_NOT_CREATED":
                    sessionStorage.setItem(paymentStatusStoreRageName, "cancel");
                    break;
            }
        });
    }

    private paymentFinished(): boolean {
        var paymentToken = sessionStorage.getItem(this.PAYMENT_STATUS_STORAGE_NAME);
        if (paymentToken) {
            return true;
        }
        return false;
    }

    private getPaymentStatus(): string {
        return sessionStorage.getItem(this.PAYMENT_STATUS_STORAGE_NAME);
    }

    private getEnvironment() : string {
        if (environment.production) {
            return "LIVE";
        }
        return "TEST";
    }

    private createBillinigAddress(responses: PaysafeCheckOutRequestModel) {
        if (responses?.country && 
            responses?.zip && 
            responses?.street &&
            responses?.city &&
            responses?.state) {
           return  {
                street: responses?.street,
                street2: responses?.street2,
                city: responses?.city,
                zip: responses?.zip,
                country: responses?.country,
                state: responses?.state
            }; 
        } else {
            return undefined;
        }
    }
}